import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  VisualPicker,
  ScrollArea,
  Skeleton,
  Switch,
  Text,
  useIsMobile,
  type Color,
  type GlyphName,
} from "@salestrak/ui";

import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter, SettingsBackBar, SettingsBackBarClearance, } from "#app/components/page-header";
import { DangerZoneGroup } from "#app/components/settings/danger-zone";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { EditableField } from "#app/components/settings/editable-field";
import {
  TypedConfirmationField,
  confirmationMatches,
} from "#app/components/settings/typed-confirmation-field";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { AccessDenied } from "#app/components/permissions/require-permission";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  TEAM_HANDLE_MAX_LENGTH,
  TEAM_HANDLE_MIN_LENGTH,
  isTeamHandle,
  normalizeTeamHandleInput,
  teamNameError,
} from "#app/lib/team-handle";

// The settings fields' name check, in the EditableField shape.
const isTeamName = (value: string) => teamNameError(value) === null;
const teamNameMessage = (value: string) => teamNameError(value) ?? "";

function TeamEditPage() {
  const isMobile = useIsMobile();
  const { teamId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { updateTeam, deleteTeam } = useTeamMutations();
  const { team, glyph, loading, error } = useTeamDetail(teamId);
  // The page admits anyone the server-computed `viewerCanManage` admits
  // (manage.teams OR a manager of this team). Privacy and Delete stay
  // workspace-level — the server rejects both for a team manager, so their
  // rows render only for manage.teams holders. The back target
  // (/settings/teams) is manage.teams-only too, so managers get no back bar.
  const canManageTeams = usePermission("manage.teams");

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // A save the server refuses (a name or handle another team holds) keeps
  // its reason under the field until the next save of that field lands, so
  // the refused value is never left looking saved.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const handleFieldSave = useCallback(
    async (fieldId: string, value: string) => {
      if (!teamId) return;
      const outcome: { refusal?: string } = {};
      await updateTeam(
        teamId,
        { [fieldId]: fieldId === "teamName" ? value.trim() : value },
        { autosave: true, onError: (message) => { outcome.refusal = message; } },
      );
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (outcome.refusal) next[fieldId] = outcome.refusal;
        else delete next[fieldId];
        return next;
      });
    },
    [teamId, updateTeam],
  );

  // Each handler writes the OTHER half from the resolved glyph verbatim —
  // glyph.icon stays null for monogram teams, so a color change never
  // silently converts the letter tile into a glyph.
  const handleIconChange = useCallback(
    async (icon: GlyphName | null) => {
      if (!teamId || !team) return;
      await updateTeam(teamId, { icon: { icon, color: glyph.color } });
    },
    [teamId, team, updateTeam, glyph.color],
  );

  const handleColorChange = useCallback(
    async (color: Color) => {
      if (!teamId || !team) return;
      await updateTeam(teamId, { icon: { icon: glyph.icon, color } });
    },
    [teamId, team, updateTeam, glyph.icon],
  );

  const handlePrivateChange = useCallback(
    async (isPrivate: boolean) => {
      if (!teamId) return;
      await updateTeam(teamId, { private: isPrivate });
    },
    [teamId, updateTeam],
  );

  const handleDelete = useCallback(async () => {
    if (!teamId) return;
    await deleteTeam(teamId);
    setDeleteOpen(false);
    replace("/settings/teams");
  }, [teamId, deleteTeam, replace]);

  if (loading && !team) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: the back bar and header geometry hold through the
          * resolve; only the team name is a bar. Groups mirror the settled
          * run — General (2 rows), Privacy, Danger zone. */}
        {canManageTeams ? (
          <SettingsBackBar label="Teams" onClick={() => replace("/settings/teams")} />
        ) : null}
        <Box fill="flex" overflow="auto">
          {canManageTeams ? <SettingsBackBarClearance /> : null}
          <PageHeader centered pinnedBack={canManageTeams}>
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton
            groups={[{ rows: 2 }, { rows: 1 }, { rows: 1 }]}
          />
        </Box>
      </Box>
    );
  }

  if (!team) {
    return <LoadErrorState noun="team" error={error ?? null} />;
  }

  if (!team.viewerCanManage) {
    return <AccessDenied />;
  }

  const canDeleteConfirm = confirmationMatches(deleteConfirmText, team.teamName);

  return (
    <Box direction="column" fill="flex" position="relative">
      {canManageTeams ? (
        <SettingsBackBar label="Teams" onClick={() => replace("/settings/teams")} />
      ) : null}
      <ScrollArea>
        {canManageTeams ? <SettingsBackBarClearance /> : null}
        <PageHeader centered pinnedBack={canManageTeams}>
          <PageHeaderTitle>{team.teamName}</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <SettingsGroup label="General">
              <SettingsGroupRow label="Name & Icon">
                <Box direction="row" gap="sm" align="center" fill={isMobile ? "width" : undefined}>
                  <VisualPicker
                    icon={glyph.icon}
                    color={glyph.color}
                    onIconChange={handleIconChange}
                    onColorChange={handleColorChange}
                    // Offered only while a glyph replaces the monogram.
                    onReset={glyph.icon === null ? undefined : () => void handleIconChange(null)}
                    size="sm"
                    // The trigger is the hairline control with the appearance
                    // in the ink — the same face the workflow's Name & Icon
                    // row wears, so the row reads as part of the settings
                    // card rather than a painted tile sitting on it.
                    variant="outline"
                    monogram={team.teamName}
                  />
                  <Box fill={isMobile ? "flex" : undefined}>
                    <EditableField
                      fieldId="teamName"
                      label="Team name"
                      initialValue={team.teamName}
                      onSave={handleFieldSave}
                      placeholder="Team name"
                      validate={isTeamName}
                      errorMessage={teamNameMessage}
                      error={fieldErrors.teamName}
                      width={isMobile ? "full" : "xl"}
                    />
                  </Box>
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Team handle"
                subtitle="The Team's unique handle. Spaces become hyphens"
              >
                <Box width={isMobile ? undefined : 232} fill={isMobile ? "width" : undefined}>
                  <EditableField
                    fieldId="teamKey"
                    label="Team handle"
                    initialValue={team.teamKey ?? ""}
                    onSave={handleFieldSave}
                    placeholder="field-crew"
                    sanitize={normalizeTeamHandleInput}
                    validate={isTeamHandle}
                    errorMessage={`Use ${TEAM_HANDLE_MIN_LENGTH}-${TEAM_HANDLE_MAX_LENGTH} lowercase letters, numbers or hyphens`}
                    error={fieldErrors.teamKey}
                    width="full"
                  />
                </Box>
              </SettingsGroupRow>
            </SettingsGroup>

            {canManageTeams ? (
              <SettingsGroup label="Privacy">
                <SettingsGroupRow
                  label="Private team"
                  subtitle="Visible only to its Members and admins"
                >
                  <Switch
                    aria-label="Private team"
                    checked={!!team.private}
                    onCheckedChange={handlePrivateChange}
                  />
                </SettingsGroupRow>
              </SettingsGroup>
            ) : null}

            {canManageTeams ? (
              <DangerZoneGroup
                dangerZone={[
                  {
                    id: "delete-team",
                    label: "Delete team",
                    subtitle:
                      "Permanently delete this team. Anything assigned to it is unassigned.",
                    buttonLabel: "Delete team...",
                    variant: "destructive",
                    onClick: () => {
                      setDeleteConfirmText("");
                      setDeleteOpen(true);
                    },
                  },
                ]}
              />
            ) : null}

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        {/* Type-to-confirm — a stray outside click must not eat the typed name. */}
        <AlertDialogPopup disablePointerDismissal>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete the {team.teamName} team?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Anything assigned to this Team is unassigned. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogPanel>
            <TypedConfirmationField
              name={team.teamName}
              noun="team"
              onChange={setDeleteConfirmText}
              onSubmit={() => void handleDelete()}
              value={deleteConfirmText}
            />
          </AlertDialogPanel>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button
              color="destructive"
              onClick={handleDelete}
              disabled={!canDeleteConfirm}
            >
              <Text>Delete Team</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/teams/$teamId/",
)({ component: TeamEditPage });
